import React, { useState, useEffect } from 'react';
import { Spinner } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { selectSubscriptionByUserId } from '../../selectors/subscription';
import * as subscriptionsActions from '../../store/subscriptions/actions';
import * as usersActions from '../../store/users/actions';
import { actualUser } from '../../selectors/user';
import { PLANS } from '../../config/settings';
import Create from '../Subscriptions/Create';
import SubscriptionInfo from '../../components/Subscriptions/SubscriptionInfo';
import Modal from '../../components/Common/Modal/Modal';
import doc24Icon from '../../assets/images/doc24-app-icon.webp';
import logo from '../../assets/images/logo-sm-dark.png';

// Import service icons
import medicoTelefonoIcon from '../../assets/images/service-icons/medico-telefono-H24.png';
import centraleOperativaIcon from '../../assets/images/service-icons/centrale-operativa.png';
import networkSanitarioIcon from '../../assets/images/service-icons/network-sanitario.png';
import networkOdontoiatricoIcon from '../../assets/images/service-icons/network-odontoiatrico.png';
import tuttaFamigliaIcon from '../../assets/images/service-icons/tutta-famiglia.png';

const SubscriberDashboard = (props) => {
  const userId = useSelector((state) => state.Auth.admin.id);
  const subscription = useSelector(selectSubscriptionByUserId(userId));
  const user = useSelector(actualUser);

  const subscriptionLoading = useSelector(
    (state) => state.Subscription.loading,
  );
  const userLoading = useSelector((state) => state.User.loading);
  const loading = subscriptionLoading || userLoading;

  const [phoneModal, setPhoneModal] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(subscriptionsActions.fetchSubscriptionsByUser(userId));
    dispatch(usersActions.syncUsers());
  }, []);

  if (loading || user?.checkIccUser === 'new')
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: '100vh' }}>
        <Spinner color="primary" />
      </div>
    );

  if (user?.checkIccUser === 'completed' && !user?.enableSubscription)
    return (
      <div className="text-center p-5">
        <div className="alert alert-warning mb-4">
          <i className="mdi mdi-alert-circle-outline mr-2"></i>
          Abbonamento già esistente
        </div>
        <h2 className="mb-4">
          Hai già un abbonamento attivo con Doc24, nostro partner in Sos Dottore
        </h2>
        <p className="text-muted">
          Non è possibile procedere con un nuovo abbonamento in quanto risulti
          già abbonato tramite Doc24.
        </p>
      </div>
    );

  if (!subscription && user?.enableSubscription) return <Create />;

  // Mappa le feature alle icone corrispondenti
  const getFeatureIcon = (feature) => {
    if (
      feature.toLowerCase().includes('televideovisita') ||
      feature.toLowerCase().includes('medico generico h24')
    ) {
      return medicoTelefonoIcon;
    } else if (
      feature.toLowerCase().includes('appuntamenti') ||
      feature.toLowerCase().includes('strutture fisiche')
    ) {
      return centraleOperativaIcon;
    } else if (feature.toLowerCase().includes('network sanitario')) {
      return networkSanitarioIcon;
    } else if (feature.toLowerCase().includes('network odontoiatrico')) {
      return networkOdontoiatricoIcon;
    } else if (
      feature.toLowerCase().includes('persone incluse') ||
      feature.toLowerCase().includes('famiglia')
    ) {
      return tuttaFamigliaIcon;
    } else {
      return logo; // fallback icon
    }
  };

  const currentPlan =
    PLANS.find((plan) => plan.stripeId === subscription?.stripePriceId) ||
    PLANS[0];

  const openPhoneModal = () => {
    setPhoneModal(true);
  };

  const handleGooglePlayDownload = () => {
    window.open(
      'https://play.google.com/store/apps/details?id=it.gesi.android.filodiretto.doc24&hl=it',
      '_blank',
    );
  };

  const handleAppStoreDownload = () => {
    window.open('https://apps.apple.com/it/app/doc-24/id1062283146', '_blank');
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <h1 className="h3 mb-4">
            Ciao {user?.firstName} {user?.lastName}
          </h1>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-8">
          <div className="card">
            <div className="card-header">
              <h3 className="mb-0">Il Tuo Abbonamento</h3>
            </div>
            <div className="card-body">
              <SubscriptionInfo subscription={subscription} />
            </div>
          </div>
        </div>

        {/* Pulsanti Azioni */}
        <div className="col-md-4">
          <div className="card">
            <div className="card-header">
              <h4 className="mb-0">Azioni Rapide</h4>
            </div>
            <div className="card-body">
              <button
                className="btn btn-primary btn-block mb-3"
                onClick={openPhoneModal}>
                <i className="fas fa-phone mr-2"></i>
                Chiama SOS Dottore
              </button>

              {/* Sezione Download App in evidenza */}
              <div className="border border-light rounded p-3 bg-light">
                <div className="text-center mb-3">
                  <img
                    src={doc24Icon}
                    alt="Doc24 App"
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '12px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                  />
                  <h4 className="my-2 mb-0">Scarica l'App Doc24</h4>
                  <p className="text-muted">
                    Accedi ai servizi di Sos Dottore ovunque tu sia
                  </p>
                </div>
                <button
                  className="btn btn-success btn-block mb-2"
                  onClick={handleGooglePlayDownload}>
                  <i className="fab fa-google-play mr-2"></i>
                  Google Play Store
                </button>
                <button
                  className="btn btn-dark btn-block"
                  onClick={handleAppStoreDownload}>
                  <i className="fab fa-apple mr-2"></i>
                  Apple App Store
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Servizi Inclusi */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h4 className="mb-0">
                Servizi Inclusi nel Tuo Piano {currentPlan.title}
              </h4>
            </div>
            <div className="card-body">
              <div className="row">
                {currentPlan.features.map((feature, index) => (
                  <div key={index} className="col-md-6 col-lg-4 mb-3">
                    <div className="card border-left-success h-100">
                      <div className="card-body">
                        <div className="d-flex align-items-start">
                          <div className="mr-3 flex-shrink-0">
                            <img
                              src={getFeatureIcon(feature)}
                              alt={feature}
                              style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '8px',
                              }}
                            />
                          </div>
                          <div>
                            <p className="card-text font-size-14 mb-0">
                              {feature}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        size="lg"
        title="Contatta SOS Dottore"
        isOpen={phoneModal}
        toggle={() => setPhoneModal(!phoneModal)}
        showClose
        scrollable={false}>
        <div className="p-4">
          <div className="text-center mb-4">
            <h4 className="mb-3">Contatta SOS Dottore</h4>
            <p className="text-muted">
              Scegli il numero da chiamare in base alla tua posizione
            </p>
          </div>

          <div className="row justify-content-center">
            <div className="col-md-6 mb-3">
              <div className="card border-primary h-100">
                <div className="card-body text-center p-4">
                  <div className="avatar-md mx-auto mb-3">
                    <span className="avatar-title rounded-circle bg-primary">
                      <i className="bx bx-phone-call font-size-24 text-white"></i>
                    </span>
                  </div>
                  <h5 className="mb-3">Numero Verde Italia</h5>
                  <p className="text-muted mb-3">
                    Chiamata gratuita da tutta Italia
                  </p>
                  <a
                    href="tel:800383098"
                    className="btn btn-primary btn-rounded waves-effect waves-light px-4">
                    <i className="bx bx-phone-call align-middle mr-2"></i>
                    800 383 098
                  </a>
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="card border-info h-100">
                <div className="card-body text-center p-4">
                  <div className="avatar-md mx-auto mb-3">
                    <span className="avatar-title rounded-circle bg-info">
                      <i className="bx bx-globe font-size-24 text-white"></i>
                    </span>
                  </div>
                  <h5 className="mb-3">Numero Internazionale</h5>
                  <p className="text-muted mb-3">
                    Chiamata a pagamento dall'estero
                  </p>
                  <a
                    href="tel:+3965546013"
                    className="btn btn-info btn-rounded waves-effect waves-light px-4">
                    <i className="bx bx-phone-call align-middle mr-2"></i>
                    +39 655 460 13
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SubscriberDashboard;
